/**
 * CreateLocationModal
 * Modal for creating a new location
 */

import React, { useState } from 'react';
import { Modal, Input, Button, Textarea } from '@/components/ui';
import { MapPin } from 'lucide-react';
import type { Location, Region, Country } from '@simcheck/shared-types';
import { createLocation } from '../../services/hierarchicalLocationService';

// The API requires an IANA timezone; the browser's is the right default for
// someone creating a site they're physically near, and stays editable.
const browserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
};

interface CreateLocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  regions: Region[];
  countries: Country[];
  userId: string;
  /**
   * Tenant mode (#202): when set, the location is created as PRIVATE to this
   * org (ownerOrgId stamped, not public). Leave unset for the super-admin
   * shared-location flow.
   */
  ownerOrgId?: string | null;
}

export const CreateLocationModal: React.FC<CreateLocationModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  regions,
  countries,
  userId,
  ownerOrgId,
}) => {
  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [regionId, setRegionId] = useState('');
  const [countryId, setCountryId] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [address, setAddress] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [timezone, setTimezone] = useState(browserTimezone);
  // Timezone is derived from the chosen country; `customTimezone` switches the
  // field back to free text for zones the country record doesn't list.
  const [customTimezone, setCustomTimezone] = useState(false);
  const [type, setType] = useState<Location['type']>('datacenter');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filteredCountries = regionId 
    ? countries.filter(c => c.regionId === regionId)
    : [];
  const selectedCountry = countries.find(c => c.id === countryId);
  const countryTimezones: string[] = selectedCountry
    ? Array.from(new Set([selectedCountry.timezone, ...(selectedCountry.timezones || [])].filter(Boolean)))
    : [];
  const showTimezoneSelect = countryTimezones.length > 0 && !customTimezone;

  const handleCountryChange = (id: string) => {
    setCountryId(id);
    setCustomTimezone(false);
    const country = countries.find(c => c.id === id);
    if (country?.timezone) setTimezone(country.timezone);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim() || !displayName.trim() || !regionId || !countryId || !city.trim() || !timezone.trim()) {
      setError('Name, display name, region, country, city, and timezone are required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Server-mediated: description / address / postal code are owner-only and
      // must land in the location's private subcollection, which only the API
      // can write. A direct Firestore create would put them on the
      // world-readable location document. The API also stamps tenancy and does
      // the parent-country bookkeeping.
      await createLocation(
        {
          name: name.trim(),
          displayName: displayName.trim(),
          description: description.trim() || undefined,
          regionId,
          countryId,
          city: city.trim(),
          state: state.trim() || undefined,
          address: address.trim() || undefined,
          postalCode: postalCode.trim() || undefined,
          timezone: timezone.trim(),
          type,
          ownerOrgId: ownerOrgId ?? null,
          organizationIds: ownerOrgId ? [ownerOrgId] : [],
          isPublic: !ownerOrgId,
          status: 'active',
          createdBy: userId,
        },
        userId,
      );

      // Reset form
      setName('');
      setDisplayName('');
      setDescription('');
      setRegionId('');
      setCountryId('');
      setCity('');
      setState('');
      setAddress('');
      setPostalCode('');
      setTimezone(browserTimezone());
      setCustomTimezone(false);
      setType('datacenter');

      onSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to create location:', err);
      setError(err instanceof Error ? err.message : 'Failed to create location');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      // Reset form
      setName('');
      setDisplayName('');
      setDescription('');
      setRegionId('');
      setCountryId('');
      setCity('');
      setState('');
      setAddress('');
      setPostalCode('');
      setTimezone(browserTimezone());
      setCustomTimezone(false);
      setType('datacenter');
      setError(null);
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Create Location"
    >
      <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
        {error && (
          <div className="p-3 bg-status-error-bg border border-status-error/30 rounded-lg">
            <p className="text-sm text-status-error">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Location Name *
            </label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Berlin Data Center 1"
              disabled={loading}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Display Name *
            </label>
            <Input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g., Berlin DC1"
              disabled={loading}
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            Description
          </label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description"
            rows={2}
            disabled={loading}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Region *
            </label>
            <select
              value={regionId}
              onChange={(e) => {
                setRegionId(e.target.value);
                setCountryId(''); // Reset country when region changes
              }}
              disabled={loading}
              required
              className="w-full px-3 py-2 bg-card border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-foreground"
            >
              <option value="">Select region...</option>
              {regions.map((region) => (
                <option key={region.id} value={region.id}>
                  {region.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Country *
            </label>
            <select
              value={countryId}
              onChange={(e) => handleCountryChange(e.target.value)}
              disabled={loading || !regionId}
              required
              className="w-full px-3 py-2 bg-card border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-foreground disabled:opacity-50"
            >
              <option value="">Select country...</option>
              {filteredCountries.map((country) => (
                <option key={country.id} value={country.id}>
                  {country.flag} {country.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              City *
            </label>
            <Input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="e.g., Berlin"
              disabled={loading}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              State/Province
            </label>
            <Input
              type="text"
              value={state}
              onChange={(e) => setState(e.target.value)}
              placeholder="e.g., NY"
              disabled={loading}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            Address
          </label>
          <Input
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Street address"
            disabled={loading}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Postal Code
            </label>
            <Input
              type="text"
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value)}
              placeholder="e.g., 10115"
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Timezone *
            </label>
            {showTimezoneSelect ? (
              <select
                value={timezone}
                onChange={(e) => {
                  if (e.target.value === '__custom') {
                    setCustomTimezone(true);
                  } else {
                    setTimezone(e.target.value);
                  }
                }}
                disabled={loading}
                required
                className="w-full px-3 py-2 bg-card border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-foreground"
              >
                {countryTimezones.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
                <option value="__custom">Other (enter manually)…</option>
              </select>
            ) : (
              <Input
                type="text"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                placeholder={selectedCountry ? `e.g., ${selectedCountry.timezone}` : 'Select a country first'}
                disabled={loading}
                required
              />
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            Location Type
          </label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as Location['type'])}
            disabled={loading}
            className="w-full px-3 py-2 bg-card border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-foreground"
          >
            <option value="datacenter">Data Center</option>
            <option value="office">Office</option>
            <option value="lab">Lab</option>
            <option value="home">Home</option>
            <option value="mobile">Mobile</option>
          </select>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="default"
            disabled={loading || !name.trim() || !displayName.trim() || !regionId || !countryId || !city.trim() || !timezone.trim()}
          >
            {loading ? 'Creating...' : 'Create Location'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

